import React from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Card,
    CardContent,
    Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';

const Welcome: React.FC = () => {
    const navigate = useNavigate();

    const features = [
        {
            icon: <CurrencyExchangeIcon sx={{ fontSize: 40 }} />,
            title: 'Real-time Exchange Rates',
            description: 'Get live currency conversion rates with automatic updates every 5 minutes.',
            color: 'primary'
        },
        {
            icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
            title: 'Iranian Market Integration',
            description: 'Specialized handling for IRR/USD rates from local Iranian sources.',
            color: 'secondary'
        },
        {
            icon: <SecurityIcon sx={{ fontSize: 40 }} />,
            title: 'Secure & Reliable',
            description: 'Multiple rate providers with fallback mechanisms for reliability.',
            color: 'success'
        },
        {
            icon: <SpeedIcon sx={{ fontSize: 40 }} />,
            title: 'Fast & Responsive',
            description: 'Optimized for speed with modern React and Material-UI components.',
            color: 'info'
        }
    ];

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                    P2P Exchange
                </Typography>
                <Typography variant="h5" color="text.secondary" gutterBottom>
                    Real-time Currency Exchange with Iranian Market Integration
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
                    Experience seamless currency conversion with live rates from multiple providers.
                    Specialized support for Iranian Rial (IRR) exchange rates alongside global currencies.
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/guest')}
                        startIcon={<CurrencyExchangeIcon />}
                        sx={{ minWidth: 200 }}
                    >
                        Start Exchange
                    </Button>
                    <Button
                        variant="outlined"
                        size="large"
                        onClick={() => navigate('/location')}
                        sx={{ minWidth: 200 }}
                    >
                        Detect Location
                    </Button>
                </Box>
            </Box>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                gap: 4,
                mb: 6
            }}>
                {features.map((feature, index) => (
                    <Card
                        key={index}
                        sx={{
                            height: '100%',
                            textAlign: 'center',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: 4,
                            }
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ color: `${feature.color}.main`, mb: 2 }}>
                                {feature.icon}
                            </Box>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                {feature.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {feature.description}
                            </Typography>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                    Supported Currencies
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mt: 2 }}>
                    {['USD', 'EUR', 'GBP', 'JPY', 'IRR', 'AUD', 'CAD', 'CHF'].map((currency) => (
                        <Chip
                            key={currency}
                            label={currency}
                            variant="outlined"
                            color="primary"
                            size="small"
                        />
                    ))}
                    <Chip label="+12 more" variant="outlined" size="small" />
                </Box>
            </Box>

            <Card sx={{ textAlign: 'center', p: 4, bgcolor: 'background.paper' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Ready to Start?
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Get instant currency conversion with real-time rates from trusted providers.
                </Typography>
                <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/guest')}
                    startIcon={<CurrencyExchangeIcon />}
                >
                    Launch Exchange Calculator
                </Button>
            </Card>
        </Container>
    );
};

export default Welcome; 